import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, type Email, type EmailEvent } from '../api';
import {
  Copyable,
  Dialog,
  ErrorNote,
  formatDateTime,
  label,
  PageHeader,
  Status,
  useToast,
} from '../components/ui';
import { useLoad } from '../hooks';

const EVENT_TONE: Record<string, string> = {
  sent: 'info',
  delivered: 'ok',
  delivery_delayed: 'warn',
  bounced: 'bad',
  complained: 'warn',
  rejected: 'bad',
  rendering_failure: 'bad',
  suppressed: 'muted',
};

const EVENT_TEXT: Record<string, string> = {
  sent: 'Handed to the mail servers',
  delivered: 'Accepted by the recipient’s server',
  delivery_delayed: 'Delivery delayed, retrying',
  bounced: 'Bounced',
  complained: 'Marked as spam',
  rejected: 'Rejected before sending',
  rendering_failure: 'Could not be rendered',
  suppressed: 'Skipped: address is suppressed',
};

/** The most useful detail of an event, in one line. */
function eventDetail(event: EmailEvent): string | null {
  const d = event.data;
  if (d.test) return 'Test mode';
  const parts = [
    d.bounceType,
    d.bounceSubType,
    d.diagnosticCode,
    d.smtpResponse,
    d.reason,
    d.message,
    d.complaintFeedbackType,
  ]
    .filter((v) => typeof v === 'string' && v)
    .map(String);
  return parts.length ? parts.join(' · ') : null;
}

export function EmailDetail() {
  const { id = '' } = useParams();
  const {
    data: email,
    error,
    loading,
    reload,
  } = useLoad(() => api<Email>('GET', `/emails/${encodeURIComponent(id)}`), [id]);
  const [tab, setTab] = useState<'preview' | 'html' | 'text'>('preview');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const toast = useToast();

  if (loading && !email) return <div className="skeleton" style={{ height: 28, width: 260 }} />;
  if (error || !email) return <ErrorNote error={error ?? 'Email not found.'} />;

  const shownTab = tab === 'preview' && !email.html ? 'text' : tab;

  return (
    <>
      <PageHeader
        crumbs={
          <>
            <Link to="/emails">Emails</Link>
            <span>/</span>
            <span className="mono">{email.id}</span>
          </>
        }
        title={email.subject || '(no subject)'}
        description={
          <>
            <Status status={email.status} />{' '}
            <span style={{ marginLeft: 8 }}>to {email.to.join(', ')}</span>
          </>
        }
        actions={
          email.status === 'scheduled' ? (
            <button type="button" className="btn btn-danger" onClick={() => setConfirmCancel(true)}>
              Cancel sending
            </button>
          ) : null
        }
      />
      {email.error ? <div className="banner">{email.error}</div> : null}

      <div className="detail-grid">
        <div>
          <div className="card">
            <div className="tabs" role="tablist">
              {(['preview', 'html', 'text'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={shownTab === t}
                  disabled={(t !== 'text' && !email.html) || (t === 'text' && !email.text)}
                  onClick={() => setTab(t)}
                >
                  {t === 'preview' ? 'Preview' : t === 'html' ? 'HTML' : 'Plain text'}
                </button>
              ))}
            </div>
            {shownTab === 'preview' && email.html ? (
              // No scripts, forms or navigation: the email's HTML is shown, never run.
              <iframe
                className="preview-frame"
                title="Email preview"
                sandbox=""
                srcDoc={email.html}
              />
            ) : shownTab === 'html' ? (
              <pre className="source">{email.html}</pre>
            ) : (
              <pre className="source">{email.text ?? 'No plain-text version.'}</pre>
            )}
          </div>
        </div>

        <div>
          <div className="card">
            <div className="card-head">
              <h2>Timeline</h2>
            </div>
            <div className="card-body">
              <ol className="timeline">
                <li>
                  <span className="dot muted" />
                  <div className="what">
                    <strong>{email.scheduledAt ? 'Scheduled' : 'Accepted by the API'}</strong>
                    <time>{formatDateTime(email.createdAt)}</time>
                    {email.scheduledAt ? (
                      <div className="detail">Sends at {formatDateTime(email.scheduledAt)}</div>
                    ) : null}
                  </div>
                </li>
                {email.events.map((event, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: events are append-only
                  <li key={i}>
                    <span className={`dot ${EVENT_TONE[event.type] ?? 'muted'}`} />
                    <div className="what">
                      <strong>{EVENT_TEXT[event.type] ?? label(event.type)}</strong>
                      <time>{formatDateTime(event.occurredAt)}</time>
                      {event.recipient ? <div className="detail">{event.recipient}</div> : null}
                      {eventDetail(event) ? (
                        <div className="detail">{eventDetail(event)}</div>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h2>Details</h2>
            </div>
            <div className="card-body">
              <dl className="facts">
                <dt>ID</dt>
                <dd>
                  <Copyable value={email.id} />
                </dd>
                <dt>From</dt>
                <dd>{email.from}</dd>
                <dt>To</dt>
                <dd>{email.to.join(', ')}</dd>
                {email.cc.length ? (
                  <>
                    <dt>Cc</dt>
                    <dd>{email.cc.join(', ')}</dd>
                  </>
                ) : null}
                {email.bcc.length ? (
                  <>
                    <dt>Bcc</dt>
                    <dd>{email.bcc.join(', ')}</dd>
                  </>
                ) : null}
                {email.replyTo.length ? (
                  <>
                    <dt>Reply to</dt>
                    <dd>{email.replyTo.join(', ')}</dd>
                  </>
                ) : null}
                <dt>Sent</dt>
                <dd>{formatDateTime(email.sentAt)}</dd>
                {email.tags.length ? (
                  <>
                    <dt>Tags</dt>
                    <dd className="chips">
                      {email.tags.map((t) => (
                        <span key={t.name} className="chip">
                          {t.name}: {t.value}
                        </span>
                      ))}
                    </dd>
                  </>
                ) : null}
                {email.attachments.length ? (
                  <>
                    <dt>Attachments</dt>
                    <dd>
                      {email.attachments.map((a) => (
                        <div key={a.filename}>
                          {a.filename}{' '}
                          <span style={{ color: 'var(--dim)' }}>
                            ({Math.ceil(a.size / 1024)} KB)
                          </span>
                        </div>
                      ))}
                    </dd>
                  </>
                ) : null}
              </dl>
            </div>
          </div>
        </div>
      </div>

      {confirmCancel ? (
        <Dialog
          title="Cancel this email?"
          description="It won't be sent. This can't be undone."
          onClose={() => setConfirmCancel(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setConfirmCancel(false)}
              >
                Keep it
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={async () => {
                  setConfirmCancel(false);
                  try {
                    await api('POST', `/emails/${encodeURIComponent(email.id)}/cancel`);
                    toast.show('Email canceled');
                    reload();
                  } catch (err) {
                    toast.show(err instanceof Error ? err.message : 'Could not cancel');
                  }
                }}
              >
                Cancel sending
              </button>
            </>
          }
        />
      ) : null}
      {toast.node}
    </>
  );
}
